import { motion } from "framer-motion";
import { CvSnapshot } from "./CvSnapshot";
import { pickImage } from "../../lib/api";
import { useLeadModal } from "@/context/LeadModalContext";

export const FeatureCard = ({ feature, tags, industries, highlighted, categoryName, categorySlug }) => {
  const { open: openLeadModal } = useLeadModal();
  const tagNames = (feature.tag_ids || [])
    .map((id) => tags.find((t) => t.id === id)?.name)
    .filter(Boolean);
  const industryNames = (feature.industry_ids || [])
    .map((id) => industries.find((i) => i.id === id)?.name)
    .filter(Boolean);

  const onAskForDemo = (e) => {
    e.stopPropagation();
    openLeadModal({
      featureId: feature.id,
      featureTitle: feature.title,
      categoryName,
      categorySlug,
    });
  };

  return (
    <motion.article
      data-testid={`feature-card-${feature.slug || feature.id}`}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative bg-white border rounded-xl overflow-hidden flex flex-col scroll-mt-32 transition-[transform,box-shadow,border-color] duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(10,10,10,0.18)] ${
        highlighted
          ? "border-brand ring-4 ring-brand/30 shadow-[0_30px_60px_-20px_rgba(114,58,186,0.45)]"
          : "border-neutral-200"
      }`}
    >
      <CvSnapshot
        image={pickImage(feature)}
        camId={feature.cam_id}
        boxes={feature.boxes || []}
        aspect="aspect-[16/10]"
      />
      <div className="p-5 sm:p-6 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {industryNames.length > 0 && (
              <div className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                {industryNames.slice(0, 2).join(" · ")}
              </div>
            )}
            <h3 className="font-display text-lg sm:text-xl font-bold text-neutral-950 tracking-tight mt-1.5">
              {feature.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onAskForDemo}
            data-testid={`feature-card-cta-${feature.slug || feature.id}`}
            aria-label={`Request a demo of ${feature.title}`}
            title={`Request a demo of ${feature.title}`}
            className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-neutral-200 bg-white text-neutral-700 hover:bg-brand hover:text-white hover:border-brand transition-all duration-200 group/btn"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5"
            >
              <line x1="7" y1="17" x2="17" y2="7" />
              <polyline points="7 7 17 7 17 17" />
            </svg>
          </button>
        </div>

        <p className="mt-2 text-sm text-neutral-600 leading-relaxed flex-1">
          {feature.description}
        </p>
        {tagNames.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {tagNames.map((t) => (
              <span
                key={t}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200"
              >
                {t}
              </span>
            ))}
          </div>
        )}
        {(feature.stat_label || feature.stat_value) && (
          <div className="mt-5 flex items-center justify-between pt-4 border-t border-neutral-100">
            <span className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">
              {feature.stat_label}
            </span>
            <span className="font-display font-bold text-neutral-950">
              {feature.stat_value}
            </span>
          </div>
        )}
      </div>
    </motion.article>
  );
};
